import type { ClientInput, PrescribedExercise } from '../types';
import type { Tracer } from '../tracer';

interface LoadConfig {
  sets: number;
  reps: number;
  rpe_target: number;
  rest_seconds: number;
  tempo: string;
}

/**
 * Load prescription — sets / reps / RPE / rest / tempo per exercise based on
 * the client's primary goal. Returns a partial PrescribedExercise (load_kg
 * stays null on first prescription; the trainer fills it after the first session
 * or the progression rule fills it from week 2 logs).
 */
export function loadPrescriptionRule(
  client: ClientInput,
  trace: Tracer,
): (i: number, isCompound: boolean) => LoadConfig {
  const baseByGoal = baseLoadForGoal(client.primary_goal);

  trace.emit(
    'load_prescription',
    `RPE ${baseByGoal.rpe_target}, ${baseByGoal.sets}×${baseByGoal.reps}`,
    `Goal: ${client.primary_goal}.`,
    { primary_goal: client.primary_goal },
  );

  // Adjust reps slightly down for the heaviest compound (first exercise of the
  // day) when strength is the goal.
  return (orderIndex, isCompound) => {
    if (client.primary_goal === 'strength' && isCompound && orderIndex === 0) {
      return { ...baseByGoal, reps: Math.max(3, baseByGoal.reps - 2), sets: baseByGoal.sets + 1 };
    }
    return baseByGoal;
  };
}

function baseLoadForGoal(goal: ClientInput['primary_goal']): LoadConfig {
  switch (goal) {
    case 'strength':
      return { sets: 4, reps: 5, rpe_target: 7, rest_seconds: 180, tempo: '2-0-X-0' };
    case 'muscle_gain':
      return { sets: 4, reps: 10, rpe_target: 7, rest_seconds: 90, tempo: '3-1-1-0' };
    case 'fat_loss':
      return { sets: 3, reps: 12, rpe_target: 7, rest_seconds: 60, tempo: '2-0-1-0' };
    case 'endurance':
      return { sets: 3, reps: 15, rpe_target: 6, rest_seconds: 45, tempo: '2-0-1-0' };
    case 'general_fitness':
      return { sets: 3, reps: 10, rpe_target: 7, rest_seconds: 75, tempo: '2-0-1-0' };
  }
}

/**
 * Apply a prescription on a per-exercise basis. Pure helper.
 */
export function applyPrescription(
  exerciseId: string,
  exerciseName: string,
  primaryMuscle: PrescribedExercise['primary_muscle'],
  movementPattern: PrescribedExercise['movement_pattern'],
  orderIndex: number,
  isCompound: boolean,
  prescribe: ReturnType<typeof loadPrescriptionRule>,
): PrescribedExercise {
  const cfg = prescribe(orderIndex, isCompound);
  return {
    exercise_id: exerciseId,
    exercise_name: exerciseName,
    primary_muscle: primaryMuscle,
    movement_pattern: movementPattern,
    order_index: orderIndex,
    sets: cfg.sets,
    reps: cfg.reps,
    load_kg: null,
    rpe_target: cfg.rpe_target,
    rest_seconds: cfg.rest_seconds,
    tempo: cfg.tempo,
    notes: '',
  };
}

export const _internal = { baseLoadForGoal };
